'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { m as motion, LazyMotion, domAnimation } from 'motion/react';
import { ResponsiveHeader } from './ResponsiveHeader';
import { ResponsiveFooter } from './ResponsiveFooter';
import { Instagram, Facebook, Linkedin, Youtube } from 'lucide-react';
import { BeforeAfterSlider } from './BeforeAfterSlider';
import { LazyVideo } from './LazyVideo';

const words = ['Web Design', 'Mobile Apps', 'AI Services', 'Digital Products'];

function AnimatedExperiences() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % words.length);
        setIsAnimating(false);
      }, 500);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <span
      className={`inline-block bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500 bg-clip-text text-transparent transition-all duration-500 ${
        isAnimating ? 'opacity-0 transform -translate-y-2' : 'opacity-100 transform translate-y-0'
      }`}
    >
      {words[currentIndex]}
    </span>
  );
}

export function ResponsiveHomepage() {
  const router = useRouter();
  const missionRef = useRef<HTMLElement>(null);

  // Animation variants for reuse
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0 },
  };

  const fadeInLeft = {
    hidden: { opacity: 0, x: -60 },
    visible: { opacity: 1, x: 0 },
  };

  const fadeInRight = {
    hidden: { opacity: 0, x: 60 },
    visible: { opacity: 1, x: 0 },
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1 },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const pulseBorderBlue = {
    animate: {
      opacity: [0.4, 0.8, 0.4],
      boxShadow: [
        '0 0 15px rgba(34, 211, 238, 0.4), inset 0 0 15px rgba(34, 211, 238, 0.2)',
        '0 0 30px rgba(34, 211, 238, 0.6), inset 0 0 25px rgba(34, 211, 238, 0.4)',
        '0 0 15px rgba(34, 211, 238, 0.4), inset 0 0 15px rgba(34, 211, 238, 0.2)',
      ],
      transition: { duration: 2.5, repeat: Infinity, ease: 'easeInOut' as const },
    },
  };

  const hologramTextBlue = {
    animate: {
      textShadow: [
        '0 0 15px rgba(34, 211, 238, 0.7), 0 0 30px rgba(59, 130, 246, 0.5)',
        '0 0 25px rgba(34, 211, 238, 0.8), 0 0 45px rgba(59, 130, 246, 0.6)',
        '0 0 15px rgba(34, 211, 238, 0.7), 0 0 30px rgba(59, 130, 246, 0.5)',
      ],
      transition: { duration: 3, repeat: Infinity, ease: 'easeInOut' as const },
    },
  };

  const hologramBorder = {
    animate: {
      opacity: [0.6, 1, 0.6],
      filter: ['hue-rotate(0deg)', 'hue-rotate(45deg)', 'hue-rotate(0deg)'],
      transition: { duration: 3, repeat: Infinity, ease: 'easeInOut' as const },
    },
  };

  const scanLineHologram = {
    animate: {
      y: ['-100%', '500%'],
      transition: { duration: 5, repeat: Infinity, ease: 'linear' as const },
    },
  };

  const scanLine = {
    animate: {
      y: ['-100%', '100%'],
      transition: { duration: 4, repeat: Infinity, ease: 'linear' as const },
    },
  };

  const pulseSlow = {
    animate: {
      opacity: [0.3, 0.5, 0.3],
      scale: [1, 1.1, 1],
      transition: { duration: 4, repeat: Infinity, ease: 'easeInOut' as const },
    },
  };

  const pulseSlowDelayed = {
    animate: {
      opacity: [0.2, 0.4, 0.2],
      scale: [1, 1.15, 1],
      transition: { duration: 5, repeat: Infinity, ease: 'easeInOut' as const },
    },
  };

  const float = {
    animate: {
      y: ['-50%', 'calc(-50% - 20px)', '-50%'],
      x: '-50%',
      transition: { duration: 6, repeat: Infinity, ease: 'easeInOut' as const },
    },
  };

  return (
    <LazyMotion features={domAnimation}>
      <div className="min-h-screen bg-zinc-900 flex flex-col relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            className="absolute top-20 left-10 w-72 h-72 bg-purple-600/10 rounded-full blur-3xl will-change-transform"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.5, 0.3],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
          <motion.div
            className="absolute bottom-20 right-10 w-96 h-96 bg-pink-600/10 rounded-full blur-3xl will-change-transform"
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.2, 0.4, 0.2],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 1,
            }}
          />
        </div>

        <ResponsiveHeader />

        {/* Hero Section */}
        <section className="relative min-h-[600px] sm:min-h-[700px] lg:min-h-[853px] flex items-center">
          {/* Background Image with Parallax Effect */}
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            // @ts-expect-error fetchPriority doesn't exist on video element typings
            fetchPriority="high"
            className="absolute inset-0 w-full h-full object-cover opacity-60 pointer-events-none"
            style={{ objectFit: 'cover' }}
          >
            <source src="/videos/landing.mp4" type="video/mp4" />
          </video>

          {/* Gradient Overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-900/90 via-zinc-900/60 to-zinc-900/20 pointer-events-none" />

          {/* Content */}
          <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 flex flex-col lg:flex-row items-center justify-between gap-12 text-center lg:text-left z-10">
            {/* Left Column (Text & CTA) */}
            <motion.div
              className="space-y-8 flex flex-col items-center lg:items-start max-w-3xl flex-1 w-full"
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              {/* Main Title */}
              <motion.div
                className="relative w-full"
                variants={fadeInUp}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              >
                <h1 className="text-[40px] sm:text-6xl md:text-5xl lg:text-[60px] font-bold text-white tracking-tight leading-[1.1] text-center lg:text-left w-full">
                  <span className="block">
                    Custom built{' '}
                    <span className="inline-block sm:inline">
                      <AnimatedExperiences />
                    </span>
                  </span>
                  <span className="block mt-2">for local businesses in</span>
                  <span className="block">Honolulu and across Hawaiʻi</span>
                </h1>
              </motion.div>

              {/* Subtitle */}
              <motion.p
                className="text-lg sm:text-xl md:text-2xl text-stone-300 max-w-2xl leading-relaxed text-center lg:text-left w-full mx-auto lg:mx-0"
                variants={fadeInUp}
                transition={{ duration: 0.8, ease: 'easeOut', delay: 0.3 }}
              >
                <span className="block">Where great design meets reliable software,</span>
                <span className="block">
                  designing conversion-based websites and apps built to scale.
                </span>
              </motion.p>

              {/* CTA Button */}
              <motion.div
                variants={fadeInUp}
                transition={{ duration: 0.8, ease: 'easeOut', delay: 0.4 }}
              >
                <button
                  onClick={() => router.push('/contact')}
                  className="px-8 py-4 text-lg bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500 text-white rounded-md hover:scale-105 transition-transform duration-300 shadow-lg shadow-purple-500/50"
                >
                  Get a Quote Now
                </button>
              </motion.div>
            </motion.div>

            {/* Right Column (Top Rated Badges) */}
            <motion.div
              className="flex flex-col items-center justify-center gap-4 lg:gap-6 pt-8 lg:pt-0 lg:ml-auto w-full lg:w-auto"
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              <motion.div variants={fadeInUp} transition={{ duration: 0.8, delay: 0.4 }} className="w-full">
                <p className="text-xs sm:text-sm text-stone-400 uppercase tracking-widest font-semibold text-center hidden lg:block">Top Rated<br/>in Hawaiʻi</p>
                <p className="text-xs sm:text-sm text-stone-400 uppercase tracking-widest font-semibold text-center lg:hidden">Top Rated in Hawaiʻi</p>
              </motion.div>
              
              <div className="flex flex-row lg:flex-col items-center justify-center gap-6 lg:gap-10">
                <motion.div className="flex flex-col items-center" variants={fadeInUp} transition={{ duration: 0.8, delay: 0.5 }}>
                  <a href="https://www.designrush.com/agency/mobile-app-design-development?location=236908" target="_blank" rel="noopener noreferrer" className="block cursor-pointer">
                    <img 
                      src="/images/Mobile App Development Secondary.webp" 
                      alt="Top Rated Mobile App Development in Hawaii" 
                      className="h-28 sm:h-32 md:h-40 lg:h-48 w-auto object-contain hover:scale-105 transition-transform duration-300 drop-shadow-2xl"
                    />
                  </a>
                </motion.div>
                <motion.div className="flex flex-col items-center" variants={fadeInUp} transition={{ duration: 0.8, delay: 0.6 }}>
                  <a href="https://www.designrush.com/agency/web-development-companies/hawaii" target="_blank" rel="noopener noreferrer" className="block cursor-pointer">
                    <img 
                      src="/images/Web Design Secondary.webp" 
                      alt="Top Rated Web Design in Hawaii" 
                      className="h-28 sm:h-32 md:h-40 lg:h-48 w-auto object-contain hover:scale-105 transition-transform duration-300 drop-shadow-2xl"
                    />
                  </a>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Services Ticker */}
        <motion.section
          className="py-8 bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-fuchsia-500/20 border-y border-cyan-400/40 overflow-hidden relative"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          {/* Holographic shimmer effect */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400/10 to-transparent pointer-events-none"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          />

          <div className="flex relative z-10">
            <div className="flex whitespace-nowrap animate-scroll">
              {[
                'UX/UI Design',
                'App Design',
                'Wireframe',
                'Website Design',
                'UX/UI Design',
                'App Design',
                'Wireframe',
                'Website Design',
                'UX/UI Design',
                'App Design',
                'Wireframe',
                'Website Design',
                'UX/UI Design',
                'App Design',
                'Wireframe',
                'Website Design',
                'UX/UI Design',
                'App Design',
                'Wireframe',
                'Website Design',
                'UX/UI Design',
                'App Design',
                'Wireframe',
                'Website Design',
              ].map((item, i) => (
                <div key={i} className="inline-flex items-center mx-8">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-fuchsia-400 to-cyan-400 text-lg sm:text-xl font-semibold">
                    {item}
                  </span>
                  <span className="ml-8 text-cyan-400 drop-shadow-[0_0_8px_rgba(34,211,238,0.8)]">
                    ✦
                  </span>
                </div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* Mission Statement */}
        <section
          ref={missionRef}
          className="px-4 sm:px-6 lg:px-8 py-12 lg:py-20 bg-zinc-800/30 overflow-hidden"
        >
          <div className="max-w-7xl mx-auto text-center">
            <motion.div
              className="relative w-full px-8 sm:px-16 lg:px-24 py-6 lg:py-10"
              initial={{ opacity: 0, scale: 0.8, rotateX: 20 }}
              whileInView={{ opacity: 1, scale: 1, rotateX: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, ease: 'easeOut', delay: 0.2 }}
              style={{ perspective: '1000px' }}
            >
              {/* Hologram Corner Brackets */}
              <motion.div
                className="absolute inset-0 pointer-events-none"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                {/* Top Left */}
                <motion.div
                  className="absolute top-0 left-0 w-12 h-12 border-t-4 border-l-4 border-cyan-400/70"
                  initial={{ scale: 0, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.4, delay: 0.1 }}
                  style={{ transformOrigin: 'top left' }}
                />
                {/* Top Right */}
                <motion.div
                  className="absolute top-0 right-0 w-12 h-12 border-t-4 border-r-4 border-cyan-400/70"
                  initial={{ scale: 0, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.4, delay: 0.1 }}
                  style={{ transformOrigin: 'top right' }}
                />
                {/* Bottom Left */}
                <motion.div
                  className="absolute bottom-0 left-0 w-12 h-12 border-b-4 border-l-4 border-cyan-400/70"
                  initial={{ scale: 0, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.4, delay: 0.15 }}
                  style={{ transformOrigin: 'bottom left' }}
                />
                {/* Bottom Right */}
                <motion.div
                  className="absolute bottom-0 right-0 w-12 h-12 border-b-4 border-r-4 border-cyan-400/70"
                  initial={{ scale: 0, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.4, delay: 0.15 }}
                  style={{ transformOrigin: 'bottom right' }}
                />
              </motion.div>

              {/* Hologram Grid Background */}
              <motion.div
                className="absolute inset-0 rounded-2xl overflow-hidden"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <div
                  className="absolute inset-0 opacity-20"
                  style={{
                    backgroundImage: `
                    linear-gradient(to right, rgba(34, 211, 238, 0.3) 1px, transparent 1px),
                    linear-gradient(to bottom, rgba(34, 211, 238, 0.3) 1px, transparent 1px)
                  `,
                    backgroundSize: '20px 20px',
                  }}
                />
              </motion.div>

              {/* Animated Hologram Layers */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-cyan-500/15 via-blue-500/20 to-cyan-500/15 backdrop-blur-sm rounded-2xl"
                initial={{ scaleY: 0, opacity: 0 }}
                whileInView={{ scaleY: 1, opacity: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, ease: 'easeOut', delay: 0.4 }}
                style={{ transformOrigin: 'top' }}
              />

              {/* Hologram Scan Lines */}
              <motion.div
                className="absolute inset-0 rounded-2xl overflow-hidden"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <motion.div
                  className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-400/25 to-transparent"
                  variants={scanLineHologram}
                  animate="animate"
                  style={{ height: '30%' }}
                />
              </motion.div>

              {/* Pulsing Hologram Border */}
              <motion.div
                className="absolute inset-0 rounded-2xl"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <motion.div
                  className="absolute inset-0 rounded-2xl border-2 border-cyan-400/40 will-change-[box-shadow,opacity]"
                  variants={pulseBorderBlue}
                  animate="animate"
                  style={{ animationDelay: '0.5s' }}
                />
              </motion.div>

              {/* Multi-layer Glow Effect */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 via-blue-500/25 to-cyan-500/20 rounded-2xl blur-2xl"
                initial={{ opacity: 0, scale: 1.2 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, ease: 'easeOut', delay: 0.5 }}
              />

              {/* Holographic Particles */}
              <motion.div
                className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                {[...Array(6)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-1 h-1 rounded-full will-change-transform"
                    style={{
                      left: `${i * 16.6}%`,
                      top: '50%',
                      background: '#22d3ee',
                      boxShadow: '0 0 10px #22d3ee',
                    }}
                    animate={{
                      y: [0, -20, 0],
                      opacity: [0, 0.7, 0],
                      scale: [0, 1.5, 0],
                    }}
                    transition={{
                      duration: 2.5,
                      repeat: Infinity,
                      delay: 2.5 + i * 0.15,
                      ease: 'easeInOut',
                    }}
                  />
                ))}
              </motion.div>

              {/* Text Content with Hologram Effect */}
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white leading-tight relative z-10">
                <motion.p
                  className="mb-2"
                  initial={{ opacity: 0, y: 20, filter: 'blur(10px)' }}
                  whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, ease: 'easeOut', delay: 0.6 }}
                >
                  <span
                    className="inline-block"
                    style={{ textShadow: '0 0 15px rgba(34, 211, 238, 0.6)' }}
                  >
                    Creating custom software that converts,
                  </span>
                </motion.p>
                <motion.p
                  className="font-semibold"
                  initial={{ opacity: 0, y: 20, filter: 'blur(10px)' }}
                  whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, ease: 'easeOut', delay: 0.8 }}
                >
                  <motion.span
                    className="inline-block bg-gradient-to-r from-cyan-300 via-blue-300 to-cyan-300 bg-clip-text text-transparent will-change-[text-shadow]"
                    variants={hologramTextBlue}
                    animate="animate"
                    style={{
                      filter: 'drop-shadow(0 0 8px rgba(34, 211, 238, 0.5))',
                    }}
                  >
                    for small businesses in Hawaiʻi
                  </motion.span>
                </motion.p>
              </h2>
            </motion.div>
          </div>
        </section>

        {/* KPRESS UI Mockup Section */}
        <section className="px-4 sm:px-6 lg:px-8 py-12 lg:py-20 min-h-[500px] sm:min-h-[600px] lg:min-h-[700px] flex items-center">
          <div className="max-w-7xl mx-auto w-full">
            <motion.div
              className="relative group rounded-bl-[40px] sm:rounded-bl-[60px] lg:rounded-bl-[80px] rounded-tr-[40px] sm:rounded-tr-[60px] lg:rounded-tr-[80px] px-6 sm:px-8 lg:px-[44px] py-12 lg:py-[44px] overflow-hidden min-h-[400px] sm:min-h-[500px] lg:min-h-[600px] flex items-center"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={scaleIn}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            >
              {/* Holographic Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 via-purple-900/40 to-cyan-900/60 backdrop-blur-xl" />

              {/* Animated Gradient Border */}
              <motion.div
                className="absolute inset-0 rounded-bl-[40px] sm:rounded-bl-[60px] lg:rounded-bl-[80px] rounded-tr-[40px] sm:rounded-tr-[60px] lg:rounded-tr-[80px] p-[2px] bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500"
                variants={hologramBorder}
                animate="animate"
              />
              <div className="absolute inset-[2px] rounded-bl-[40px] sm:rounded-bl-[60px] lg:rounded-bl-[80px] rounded-tr-[40px] sm:rounded-tr-[60px] lg:rounded-tr-[80px] bg-gradient-to-br from-slate-900/95 via-purple-900/50 to-cyan-900/70 backdrop-blur-xl" />

              {/* Grid Pattern Overlay */}
              <div
                className="absolute inset-0 opacity-10"
                style={{
                  backgroundImage: `linear-gradient(rgba(139, 92, 246, 0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(139, 92, 246, 0.5) 1px, transparent 1px)`,
                  backgroundSize: '30px 30px',
                }}
              />

              {/* Scan Lines Effect */}
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <motion.div
                  className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-400/10 to-transparent"
                  variants={scanLine}
                  animate="animate"
                />
              </div>

              {/* Holographic Glow Spots */}
              <motion.div
                className="absolute top-0 left-0 w-64 h-64 bg-cyan-500/20 rounded-full blur-3xl"
                variants={pulseSlow}
                animate="animate"
              />
              <motion.div
                className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"
                variants={pulseSlowDelayed}
                animate="animate"
              />
              <motion.div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-pink-500/10 rounded-full blur-3xl"
                variants={float}
                animate="animate"
              />

              {/* Shimmer Effect on Hover */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out" />

              {/* Content */}
              <div className="relative z-10 flex flex-col xl:flex-row gap-8 xl:gap-[40px] items-center xl:items-start w-full">
                {/* Text Content */}
                <motion.div
                  className="flex-shrink-0 w-full xl:max-w-[463px]"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={fadeInLeft}
                  transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
                >
                  <div className="text-white text-lg sm:text-xl md:text-2xl lg:text-[30px] leading-relaxed lg:leading-[40px] tracking-[-0.225px] drop-shadow-[0_0_10px_rgba(139,92,246,0.5)]">
                    <p className="mb-0">
                      Whether you're dealing with an outdated website, an underperforming mobile
                      app, or you're exploring a new idea to invest in...
                    </p>
                    <p className="mt-6 lg:mt-8">
                      Our team is dedicated to elevating your digital systems by utilizing the
                      latest technologies, to make your business more efficient.
                    </p>
                  </div>
                </motion.div>

                {/* KPRESS Combined Mockup */}
                <motion.div
                  className="flex-shrink-0 w-full xl:w-auto xl:flex-1 xl:max-w-[646px] flex items-center justify-center"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={fadeInRight}
                  transition={{ duration: 0.8, ease: 'easeOut', delay: 0.4 }}
                >
                  <div className="relative w-full max-w-[600px] xl:max-w-none xl:w-full aspect-[646/439] overflow-hidden rounded-[15px] sm:rounded-[18px] lg:rounded-[20px] shadow-[0_0_30px_rgba(139,92,246,0.6)] hover:shadow-[0_0_50px_rgba(139,92,246,0.8)] transition-shadow duration-300">
                    {/* Holographic Border */}
                    <div className="absolute inset-0 rounded-[15px] sm:rounded-[18px] lg:rounded-[20px] bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 p-[2px]">
                      <div className="w-full h-full rounded-[13px] sm:rounded-[16px] lg:rounded-[18px] bg-slate-900 overflow-hidden">
                        <BeforeAfterSlider
                          beforeSrc="/videos/HawaiiLoaCareBefore_small.mp4"
                          afterSrc="/videos/HawaiiLoaCareAfter_small.mp4"
                          poster="/images/HawaiiLoaCarePreview.webp"
                          className="relative w-full h-full select-none"
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Projects Preview */}
        <section className="px-4 sm:px-6 lg:px-8 py-12 lg:py-20 bg-zinc-800/30">
          <div className="max-w-7xl mx-auto">
            <motion.h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center text-white mb-12"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              Building Mobile and Web Applications
            </motion.h2>
            <div className="grid md:grid-cols-2 gap-8">
              {/* Mobile Apps Card */}
              <motion.div
                className="group relative overflow-hidden rounded-3xl bg-zinc-800 border-2 border-purple-600/20 hover:border-purple-600 transition-all duration-300 cursor-pointer will-change-transform"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{
                  duration: 0.8,
                  ease: [0.22, 1, 0.36, 1],
                  delay: 0.1,
                }}
                whileHover={{
                  y: -10,
                  scale: 1.02,
                  transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
                }}
                onClick={() => {
                  router.push('/projects');
                  // Delay scroll to allow page transition
                  setTimeout(() => {
                    const element = document.querySelector('[data-section="native-web-apps"]');
                    if (element) {
                      const headerOffset = -58;
                      const elementPosition = element.getBoundingClientRect().top;
                      const offsetPosition = elementPosition + window.scrollY - headerOffset;

                      window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth',
                      });
                    }
                  }, 100);
                }}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src="/images/54ce6a9ef75d51cfa96aaa78e672f88218f8fbc8.webp"
                    srcSet="/images/54ce6a9ef75d51cfa96aaa78e672f88218f8fbc8-640w.webp 640w, /images/54ce6a9ef75d51cfa96aaa78e672f88218f8fbc8-768w.webp 768w, /images/54ce6a9ef75d51cfa96aaa78e672f88218f8fbc8-1024w.webp 1024w, /images/54ce6a9ef75d51cfa96aaa78e672f88218f8fbc8-1280w.webp 1280w, /images/54ce6a9ef75d51cfa96aaa78e672f88218f8fbc8-1536w.webp 1536w"
                    sizes="(max-width: 768px) calc(100vw - 32px), 50vw"
                    alt="Custom Mobile App Development Services Example"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-white mb-2">Mobile Apps</h3>
                  <p className="text-stone-300">Custom mobile solutions for iOS and Android</p>
                </div>
              </motion.div>

              {/* Website Designs Card */}
              <motion.div
                className="group relative overflow-hidden rounded-3xl bg-zinc-800 border-2 border-purple-600/20 hover:border-purple-600 transition-all duration-300 cursor-pointer will-change-transform"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{
                  duration: 0.8,
                  ease: [0.22, 1, 0.36, 1],
                  delay: 0.2,
                }}
                whileHover={{
                  y: -10,
                  scale: 1.02,
                  transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
                }}
                onClick={() => {
                  router.push('/projects');
                  // Delay scroll to allow page transition
                  setTimeout(() => {
                    const element = document.querySelector('[data-section="websites"]');
                    if (element) {
                      const headerOffset = 120;
                      const elementPosition = element.getBoundingClientRect().top;
                      const offsetPosition = elementPosition + window.scrollY - headerOffset;

                      window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth',
                      });
                    }
                  }, 100);
                }}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src="/images/3f162eedca63144c431f4fce871a19f5fea551a3.webp"
                    srcSet="/images/3f162eedca63144c431f4fce871a19f5fea551a3-640w.webp 640w, /images/3f162eedca63144c431f4fce871a19f5fea551a3-768w.webp 768w, /images/3f162eedca63144c431f4fce871a19f5fea551a3.webp 961w"
                    sizes="(max-width: 768px) calc(100vw - 32px), 50vw"
                    alt="Modern Responsive Website Design Example"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-white mb-2">Website Designs</h3>
                  <p className="text-stone-300">Modern, responsive websites that convert</p>
                </div>
              </motion.div>
            </div>

            <div className="mt-12 text-center"></div>
          </div>
        </section>

        {/* KPRESS Case Study - Exploded View */}
        <section className="px-4 sm:px-6 lg:px-8 py-12 lg:py-24 overflow-hidden bg-zinc-900 relative">
          {/* Background Elements */}
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-purple-900/10 to-transparent pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none" />

          <div className="max-w-7xl mx-auto w-full relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-10 items-center">
              {/* Text Content */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
                className="order-2 lg:order-1"
              >
                <motion.div
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-600/10 border border-purple-600/20 text-purple-300 text-sm font-semibold mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-600 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-500"></span>
                  </span>
                  FEATURED CASE STUDY
                </motion.div>

                <h2 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  Legal Consultation <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-500 to-cyan-400 animate-gradient-x">
                    Website Case Study
                  </span>
                </h2>

                {/* The Challenge Section */}
                <motion.div
                  className="mb-10"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                >
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <div className="w-8 h-px bg-gradient-to-r from-purple-500 to-cyan-500" />
                    The Challenge
                  </h3>
                  <ul className="space-y-4">
                    {[
                      {
                        title: 'Outdated Digital Presence',
                        description:
                          'The site before was confusing, simple, and not mobile friendly.',
                      },
                      {
                        title: 'Needed more qualified leads',
                        description: "Visitor traffic wasn't translating into strong leads.",
                      },
                      {
                        title: 'Performance Issues',
                        description:
                          'Slow load times and a confusing landing page were causing potential clients to bounce.',
                      },
                    ].map((item, index) => (
                      <motion.li
                        key={index}
                        className="flex items-start gap-3 group"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.4 + index * 0.1 }}
                      >
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-gradient-to-r from-purple-500 to-cyan-500 flex-shrink-0 group-hover:scale-150 transition-transform duration-300" />
                        <p className="text-stone-300 leading-relaxed">
                          <strong className="text-white group-hover:text-purple-300 transition-colors duration-300">
                            {item.title}:
                          </strong>{' '}
                          {item.description}
                        </p>
                      </motion.li>
                    ))}
                  </ul>
                </motion.div>

                {/* The Solution Section */}
                <div className="mb-8 relative">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <div className="w-8 h-px bg-gradient-to-r from-cyan-500 to-purple-500" />
                    The Solution
                  </h3>
                  <p className="text-md text-stone-300 leading-relaxed max-w-lg">
                    For KPRESS Law, we didn’t just build a website — we guided her through the
                    process of transforming it into a platform that truly reflects her brand.
                    Together, we modernized and strategically repositioned her site to be more
                    competitive in today’s probate legal industry. By combining high-performance
                    technical architecture with refined, trust-building design, we created a
                    professional digital presence that builds credibility and turns visitors into
                    clients.
                  </p>
                </div>

                <motion.a
                  href="https://www.kpresslaw.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 px-8 py-4 bg-white text-zinc-900 rounded-full font-bold text-lg hover:bg-zinc-200 transition-all shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:shadow-[0_0_30px_rgba(255,255,255,0.5)]"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  View Live Site
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </motion.a>
              </motion.div>

              {/* 3D Exploded Stack Visual and Results */}
              <div className="order-1 lg:order-2 flex flex-col gap-10 lg:gap-6">
                <motion.div
                  className="relative w-full flex items-center justify-center"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                  style={{ perspective: 1000 }}
                >
                  <div className="relative w-full h-full flex items-center justify-center group preserve-3d">
                    {/* Holographic Border for Image */}
                    <div className="relative w-[85vw] sm:w-[450px] lg:w-[700px] aspect-[646/439] rounded-[15px] sm:rounded-[18px] lg:rounded-[20px] bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 p-[2px]">
                      <div className="w-full h-full rounded-[13px] sm:rounded-[16px] lg:rounded-[18px] bg-slate-900 overflow-hidden">
                        <img
                          src="/images/458aa8e102e503d704ce969c4eb04d36b19b6cc3.webp"
                          srcSet="/images/458aa8e102e503d704ce969c4eb04d36b19b6cc3-640w.webp 640w, /images/458aa8e102e503d704ce969c4eb04d36b19b6cc3-768w.webp 768w, /images/458aa8e102e503d704ce969c4eb04d36b19b6cc3-1024w.webp 1024w, /images/458aa8e102e503d704ce969c4eb04d36b19b6cc3-1280w.webp 1280w, /images/458aa8e102e503d704ce969c4eb04d36b19b6cc3.webp 1536w"
                          sizes="(max-width: 640px) 85vw, (max-width: 1024px) 80vw, 60vw"
                          alt="KPRESS Probate Website Combined Mockup"
                          className="w-full h-full object-contain"
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Results Stats Grid */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                >
                  <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                    <div className="w-8 h-px bg-gradient-to-r from-purple-500 to-cyan-500" />
                    Results by Google Analytics
                  </h3>
                  <div className="grid grid-cols-3 gap-3 sm:gap-4">
                    <div className="p-4 sm:p-5 rounded-lg sm:rounded-2xl bg-zinc-800 border border-white/5 backdrop-blur-sm hover:bg-zinc-800/80 transition-colors duration-300 group">
                      <div className="text-2xl sm:text-3xl font-bold text-white mb-1 group-hover:text-purple-400 transition-colors">
                        152
                      </div>
                      <div className="text-xs sm:text-sm text-stone-400">New Users</div>
                    </div>
                    <div className="p-4 sm:p-5 rounded-lg sm:rounded-2xl bg-zinc-800 border border-white/5 backdrop-blur-sm hover:bg-zinc-800/80 transition-colors duration-300 group">
                      <div className="text-2xl sm:text-3xl font-bold text-white mb-1 group-hover:text-cyan-400 transition-colors">
                        130
                      </div>
                      <div className="text-xs sm:text-sm text-stone-400">
                        Active / Returning Users
                      </div>
                    </div>
                    <div className="p-4 sm:p-5 rounded-lg sm:rounded-2xl bg-zinc-800 border border-white/5 backdrop-blur-sm hover:bg-zinc-800/80 transition-colors duration-300 group">
                      <div className="text-2xl sm:text-3xl font-bold text-white mb-1 group-hover:text-pink-400 transition-colors">
                        1m 58s
                      </div>
                      <div className="text-xs sm:text-sm text-stone-400">Avg. Bounce Time</div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* Hawaii Loa Care Case Study */}
        <section className="px-4 sm:px-6 lg:px-8 py-12 lg:py-24 overflow-hidden bg-zinc-800/30 relative">
          {/* Background Elements */}
          <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-r from-emerald-900/10 to-transparent pointer-events-none" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none" />

          <div className="max-w-7xl mx-auto w-full relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-10 items-center">
              {/* Video Preview */}
              <div className="order-1 flex flex-col gap-10 lg:gap-6">
                <motion.div
                  className="relative w-full flex items-center justify-center"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                  style={{ perspective: 1000 }}
                >
                  <div className="relative w-full h-full flex items-center justify-center group preserve-3d">
                    {/* Holographic Border for Video */}
                    <div className="relative w-[85vw] sm:w-[450px] lg:w-[700px] aspect-[646/439] rounded-[15px] sm:rounded-[18px] lg:rounded-[20px] bg-gradient-to-r from-emerald-500 via-cyan-500 to-teal-400 p-[2px] shadow-[0_0_30px_rgba(16,185,129,0.4)] hover:shadow-[0_0_50px_rgba(16,185,129,0.6)] transition-shadow duration-300">
                      <div className="w-full h-full rounded-[13px] sm:rounded-[16px] lg:rounded-[18px] bg-slate-900 overflow-hidden">
                        <LazyVideo
                          src="/videos/HawaiiLoaCareAfter_small.mp4"
                          poster="/images/HawaiiLoaCarePreview.webp"
                          className="w-full h-full bg-slate-900"
                          videoClassName="object-contain"
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Results Stats Grid */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                >
                  <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                    <div className="w-8 h-px bg-gradient-to-r from-emerald-500 to-cyan-500" />
                    Results by Web & Google Analytics
                  </h3>
                  <div className="grid grid-cols-2 gap-3 sm:gap-6">
                    <div className="p-6 sm:p-5 rounded-lg sm:rounded-2xl bg-zinc-800 border border-white/5 backdrop-blur-sm hover:bg-zinc-800/80 transition-colors duration-300 group">
                      <div className="text-xl sm:text-3xl font-bold text-white mb-1 group-hover:text-emerald-400 transition-colors">
                        Approx 1,820 Unique Users
                      </div>
                      <div className="text-xs sm:text-sm text-stone-400">
                        During the first month after launch
                      </div>
                    </div>
                    <div className="p-6 sm:p-5 rounded-lg sm:rounded-2xl bg-zinc-800 border border-white/5 backdrop-blur-sm hover:bg-zinc-800/80 transition-colors duration-300 group">
                      <div className="text-xl sm:text-3xl font-bold text-white mb-1 group-hover:text-cyan-400 transition-colors">
                        Reduced Loading time to 1.5 seconds
                      </div>
                      <div className="text-xs sm:text-sm text-stone-400">
                        Increase in performance based on Google Lighthouse Scores
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Text Content */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
                className="order-2"
              >
                <motion.div
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-600/10 border border-emerald-600/20 text-emerald-300 text-sm font-semibold mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-600 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                  </span>
                  FEATURED CASE STUDY
                </motion.div>

                <h2 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  Care Home <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-cyan-400 to-teal-300 animate-gradient-x">
                    Website Case Study
                  </span>
                </h2>

                {/* The Challenge Section */}
                <motion.div
                  className="mb-10"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                >
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <div className="w-8 h-px bg-gradient-to-r from-emerald-500 to-cyan-500" />
                    The Challenge
                  </h3>
                  <ul className="space-y-4">
                    {[
                      {
                        title: 'Outdated Online Presence',
                        description:
                          "Their existing site lacked trust signals and didn't reflect the quality of care they provide.",
                      },
                      {
                        title: 'Blog Issues',
                        description:
                          "The blog posts had topics that wasn't supporting her website, not related to her mission and services.",
                      },
                      {
                        title: 'Not Mobile Friendly',
                        description:
                          'A large portion of users browsing on mobile had a poor experience, leading to high bounce rates.',
                      },
                    ].map((item, index) => (
                      <motion.li
                        key={index}
                        className="flex items-start gap-3 group"
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.4 + index * 0.1 }}
                      >
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 flex-shrink-0 group-hover:scale-150 transition-transform duration-300" />
                        <p className="text-stone-300 leading-relaxed">
                          <strong className="text-white group-hover:text-emerald-300 transition-colors duration-300">
                            {item.title}:
                          </strong>{' '}
                          {item.description}
                        </p>
                      </motion.li>
                    ))}
                  </ul>
                </motion.div>

                {/* The Solution Section */}
                <div className="mb-8 relative">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <div className="w-8 h-px bg-gradient-to-r from-cyan-500 to-emerald-500" />
                    The Solution
                  </h3>
                  <p className="text-md text-stone-300 leading-relaxed max-w-lg">
                    For Hawaii Loa Care, we rebuilt their digital presence from the ground up —
                    crafting a warm, professional website that communicates trust and compassion at
                    every touchpoint. We streamlined the resident journey, optimized for
                    mobile-first browsing, and implemented a clean, modern design that reflects the
                    high standard of care the team provides This resulted in the clients getting
                    more traffic, contact requests, and improved digtial presence.
                  </p>
                </div>

                <motion.a
                  href="https://www.hawaiiloacare.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 px-8 py-4 bg-white text-zinc-900 rounded-full font-bold text-lg hover:bg-zinc-200 transition-all shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:shadow-[0_0_30px_rgba(255,255,255,0.5)]"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  View Live Site
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </motion.a>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Client Testimonial Section */}
        <section className="px-4 sm:px-6 lg:px-8 py-12 lg:py-20 bg-zinc-900 border-t border-white/5 relative overflow-hidden">
          {/* Background Gradients */}
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-purple-500/50 to-transparent" />
          <div className="absolute inset-0 pointer-events-none">
            <motion.div
              className="absolute top-1/2 left-1/4 w-96 h-96 bg-purple-600/10 rounded-full blur-[100px]"
              animate={{
                opacity: [0.3, 0.5, 0.3],
                scale: [1, 1.2, 1],
              }}
              transition={{ duration: 8, repeat: Infinity }}
            />
            <motion.div
              className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-600/10 rounded-full blur-[100px]"
              animate={{
                opacity: [0.3, 0.5, 0.3],
                scale: [1, 1.2, 1],
              }}
              transition={{ duration: 8, repeat: Infinity, delay: 4 }}
            />
          </div>

          <div className="max-w-7xl mx-auto relative z-10">
            <div className="grid md:grid-cols-2 lg:grid-cols-1 gap-8 lg:gap-12">
              {[
                {
                  text: (
                    <>
                      "
                      <span className="text-purple-300">
                        Zumify put together a beautiful new website
                      </span>{' '}
                      for my probate business, which I believe has been helpful in attracting new
                      leads and clients. They are professional, quick to respond to all inquiries
                      and requests, courteous and caring. I was most impressed with the use of the
                      newest techniques in design and website construction. I am extremely happy
                      with the results from the Zumify team!"
                    </>
                  ),
                  author: 'Victoria, KPRESS Law',
                  role: 'Probate & Estate Planning',
                  delay: 0,
                },
                {
                  text: (
                    <>
                      "
                      <span className="text-purple-300">
                        Hawaii Rugby Union administration would like to thank Wolfgang Sakamaki
                      </span>{' '}
                      for his vision , graphics and timely work on our website. A 5 star rating. Our
                      website is now, precisely what is needed to showcase Hawaii to the Global
                      Rugby market."
                    </>
                  ),
                  author: 'Hawaii Rugby Union',
                  role: 'Rugby Non-Profit Organization',
                  delay: 0.2,
                },
                {
                  text: (
                    <>
                      "Wolfgang did an amazing job with my site. He was very professional and helped me craft a custom page for my music! He tailored reference sites I had in mind and helped me create and optimize a clean and beautiful site. Thank you Wolfgang!"
                    </>
                  ),
                  author: 'Logyn Okuda',
                  role: 'Composer',
                  delay: 0.4,
                },
              ].map((review, index) => (
                <motion.div
                  key={index}
                  className="text-center bg-zinc-800/30 rounded-3xl p-8 border border-white/5 backdrop-blur-sm"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: review.delay }}
                >
                  {/* Stars */}
                  <div className="flex justify-center gap-2 mb-8">
                    {[...Array(5)].map((_, i) => (
                      <motion.svg
                        key={i}
                        className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-400 fill-current drop-shadow-[0_0_10px_rgba(250,204,21,0.5)]"
                        viewBox="0 0 24 24"
                        initial={{ opacity: 0, scale: 0 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 + i * 0.1, type: 'spring' }}
                      >
                        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                      </motion.svg>
                    ))}
                  </div>

                  {/* Quote */}
                  <motion.blockquote
                    className="text-lg font-medium text-white mb-10 leading-normal"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.6 + review.delay }}
                  >
                    {review.text}
                  </motion.blockquote>

                  {/* Client Info */}
                  <motion.div
                    className="flex flex-col items-center gap-2"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.8 + review.delay }}
                  >
                    <cite className="not-italic text-lg font-bold text-white">{review.author}</cite>
                    <span className="text-stone-400">{review.role}</span>
                  </motion.div>
                </motion.div>
              ))}
            </div>

            {/* Third review — full width */}
            <motion.div
              className="mt-8 lg:mt-10 text-center bg-zinc-800/30 rounded-3xl p-8 border border-white/5 backdrop-blur-sm"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
            >
              {/* Stars */}
              <div className="flex justify-center gap-2 mb-8">
                {[...Array(5)].map((_, i) => (
                  <motion.svg
                    key={i}
                    className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-400 fill-current drop-shadow-[0_0_10px_rgba(250,204,21,0.5)]"
                    viewBox="0 0 24 24"
                    initial={{ opacity: 0, scale: 0 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 + i * 0.1, type: 'spring' }}
                  >
                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                  </motion.svg>
                ))}
              </div>

              {/* Full quote */}
              <motion.blockquote
                className="text-lg font-medium text-white mb-10 leading-relaxed max-w-4xl mx-auto"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
              >
                &ldquo;We&rsquo;re a local mom-and-pop business, and for a long time, our website
                was something we knew needed help but didn&rsquo;t quite know where to start. It was
                outdated, a little disorganized, and honestly wasn&rsquo;t doing much for us online.{' '}
                <span className="text-purple-300">
                  Working with Zumify made the whole process feel easy.
                </span>{' '}
                Their team was patient, helpful, and genuinely cared about helping us create a
                website that better represents who we are and attracts more customers. They showed
                aloha from start to finish, and we felt supported every step of the way. I was
                honestly beyond impressed with how everything turned out. They&rsquo;re a small
                team, but they work hard, move quickly, and pay attention to the details. Our
                website was finished in less than two weeks, and the final result was better than we
                could have imagined.{' '}
                <span className="text-purple-300">We&rsquo;re so grateful we found Zumify</span> and
                would highly recommend them to any small business looking to improve their online
                presence.&rdquo;
              </motion.blockquote>

              {/* Client Info */}
              <motion.div
                className="flex flex-col items-center gap-2"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.8 }}
              >
                <cite className="not-italic text-lg font-bold text-white">Waiola Shave Ice</cite>
                <span className="text-stone-400">Shave Ice Business, Hawaiʻi</span>
              </motion.div>
            </motion.div>

            {/* Google Reviews Link */}
            <motion.div
              className="mt-12 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <a
                href="https://g.page/r/CQ21vPgDDtzxEAE/review"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-stone-300 hover:text-white transition-colors group"
              >
                <span className="border-b border-transparent group-hover:border-white transition-all">
                  See our reviews on Google
                </span>
                <svg
                  className="w-4 h-4 transform group-hover:translate-x-1 transition-transform"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                  />
                </svg>
              </a>
            </motion.div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="relative px-4 sm:px-6 lg:px-8 py-16 lg:py-24 overflow-hidden">
          {/* Animated Background Elements */}
          <div className="absolute inset-0 pointer-events-none">
            <motion.div
              className="absolute top-1/4 left-10 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl will-change-transform"
              animate={{
                y: [0, 50, 0],
                scale: [1, 1.2, 1],
                opacity: [0.2, 0.4, 0.2],
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
            <motion.div
              className="absolute bottom-1/4 right-10 w-80 h-80 bg-cyan-500/20 rounded-full blur-3xl will-change-transform"
              animate={{
                y: [0, -50, 0],
                scale: [1, 1.3, 1],
                opacity: [0.2, 0.3, 0.2],
              }}
              transition={{
                duration: 12,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 1,
              }}
            />
          </div>

          <div className="max-w-7xl mx-auto relative z-10">
            {/* Section Header */}
            <motion.div
              className="text-center mb-16 lg:mb-20"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.5 }}
              variants={fadeInUp}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl text-white mb-4 font-bold">
                Why Choose{' '}
                <span className="bg-gradient-to-r from-purple-400 via-pink-500 to-cyan-400 bg-clip-text text-transparent">
                  Zumify
                </span>
                ?
              </h2>
              <div className="h-1 w-24 bg-gradient-to-r from-purple-500 to-cyan-500 mx-auto rounded-full" />
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              {/* Text Content with Glassmorphic Card */}
              <motion.div
                className="order-2 lg:order-1"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInLeft}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              >
                <div className="relative group">
                  {/* Glassmorphic Background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-900/30 via-slate-900/40 to-cyan-900/30 backdrop-blur-xl rounded-3xl" />
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-cyan-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  {/* Border Glow */}
                  <div className="absolute inset-0 rounded-3xl p-[2px] bg-gradient-to-r from-purple-500 via-pink-500 to-cyan-500 opacity-50 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-full h-full rounded-3xl bg-zinc-900/80 backdrop-blur-xl" />
                  </div>

                  {/* Content */}
                  <div className="relative p-8 lg:p-10 space-y-6">
                    <h3 className="text-2xl sm:text-3xl lg:text-4xl text-white leading-tight">
                      We're a{' '}
                      <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                        dedicated local team
                      </span>{' '}
                      with strong expertise and a proven track record
                    </h3>

                    <p className="text-base sm:text-lg text-stone-300/90 leading-relaxed">
                      We use modern technology stacks to build powerful, scalable, and secure
                      applications that meet your specific business needs.
                    </p>

                    <motion.ul
                      className="space-y-4 pt-4"
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      variants={staggerContainer}
                    >
                      {[
                        { text: 'Custom-built solutions tailored to your needs' },
                        { text: 'Local Hawaiʻi-based team that understands your market' },
                        {
                          text: 'We turn ideas into powerful products that help your business grow smarter and faster',
                        },
                      ].map((item, index) => (
                        <motion.li
                          key={index}
                          className="flex items-start gap-4 group/item hover:translate-x-2 transition-transform duration-300"
                          variants={fadeInLeft}
                          transition={{ duration: 0.5 }}
                        >
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center mt-0.5 group-hover/item:scale-110 transition-transform duration-300 shadow-lg shadow-purple-500/50">
                            <span className="text-white text-sm">✓</span>
                          </div>
                          <span className="text-stone-300 group-hover/item:text-white transition-colors duration-300">
                            {item.text}
                          </span>
                        </motion.li>
                      ))}
                    </motion.ul>

                    {/* Decorative Elements */}
                    <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />
                    <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />
                  </div>
                </div>
              </motion.div>

              {/* Image Grid with Enhanced Effects */}
              <motion.div
                className="order-1 lg:order-2"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={staggerContainer}
              >
                <div className="grid grid-cols-2 gap-4 lg:gap-6">
                  {/* Image 1 */}
                  <motion.div
                    className="group relative overflow-hidden rounded-2xl lg:rounded-3xl aspect-square transform hover:scale-105 transition-all duration-500 hover:z-10"
                    variants={scaleIn}
                    transition={{ duration: 0.6 }}
                  >
                    {/* Gradient Border */}
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500 via-pink-500 to-cyan-500 p-[3px] rounded-2xl lg:rounded-3xl">
                      <div className="w-full h-full rounded-2xl lg:rounded-3xl overflow-hidden bg-zinc-900">
                        <img
                          src="/images/BacktoBack.webp"
                          srcSet="/images/BacktoBack-640w.webp 640w, /images/BacktoBack-768w.webp 768w, /images/BacktoBack-1024w.webp 1024w, /images/BacktoBack.webp 1280w"
                          sizes="(max-width: 1024px) 50vw, 25vw"
                          alt="Example image of Zumify's back to back Instagram growth"
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                          loading="lazy"
                          decoding="async"
                        />
                        {/* Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-purple-900/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      </div>
                    </div>
                    {/* Glow Effect */}
                    <div className="absolute inset-0 shadow-lg shadow-purple-500/0 group-hover:shadow-purple-500/50 transition-shadow duration-500 rounded-2xl lg:rounded-3xl" />
                  </motion.div>

                  {/* Image 2 */}
                  <motion.div
                    className="group relative overflow-hidden rounded-2xl lg:rounded-3xl aspect-square transform hover:scale-105 transition-all duration-500 hover:z-10 mt-8"
                    variants={scaleIn}
                    transition={{ duration: 0.6, delay: 0.2 }}
                  >
                    {/* Gradient Border */}
                    <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 via-purple-500 to-pink-500 p-[3px] rounded-2xl lg:rounded-3xl">
                      <div className="w-full h-full rounded-2xl lg:rounded-3xl overflow-hidden bg-zinc-900">
                        <img
                          src="/images/SurfingBoth.webp"
                          srcSet="/images/SurfingBoth-640w.webp 640w, /images/SurfingBoth-768w.webp 768w, /images/SurfingBoth-1024w.webp 1024w, /images/SurfingBoth.webp 1280w"
                          sizes="(max-width: 1024px) 50vw, 25vw"
                          alt="Example image of Zumify's founders surfing posing"
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 rotate-270"
                          loading="lazy"
                          decoding="async"
                        />
                        {/* Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-cyan-900/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      </div>
                    </div>
                    {/* Glow Effect */}
                    <div className="absolute inset-0 shadow-lg shadow-cyan-500/0 group-hover:shadow-cyan-500/50 transition-shadow duration-500 rounded-2xl lg:rounded-3xl" />
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Showcase Collage Section */}
        <section className="relative w-full z-10 py-12 lg:py-20">
          <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col items-center">
              
              {/* Top: Showcase Collage */}
              <div className="w-full max-w-6xl flex flex-col gap-8 lg:gap-2">

                {/* Mobile Showcase Videos */}
                <div className="w-full flex flex-col gap-6 lg:gap-8 px-4 sm:px-0 mt-4 lg:mt-6">
                  <h3 className="text-2xl sm:text-3xl text-white font-medium text-center drop-shadow-md">
                    Effective mobile designs for your business
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 lg:gap-10">
                    {[
                      { src: "/videos/HawaiiLoaCareMobile.mp4", title: "Hawaii Loa Care", href: "https://hawaiiloacare.com/"},
                      { src: "/videos/LogynMobile.MP4", title: "Logyn Okuda", href: "https://logynokuda.com/" },
                      { src: "/videos/WaiolaMobile.mp4", title: "Waiola Shave Ice", href: "https://www.waiolashaveice.co/"}
                    ].map((video, idx) => {
                      const content = (
                        <>
                          <p className="text-white/90 text-sm md:text-base font-medium tracking-wide group-hover:text-[#00C3D0] transition-colors text-center w-full">{video.title}</p>
                          <div className="relative w-full max-w-[320px] sm:max-w-none aspect-[9/16] rounded-[36px] overflow-hidden bg-black/40 border-2 border-white/10 hover:border-[#00C3D0]/70 transition-all duration-500 shadow-xl hover:shadow-[0_0_30px_rgba(0,195,208,0.3)]">
                            <video
                              autoPlay
                              loop
                              muted
                              playsInline
                              className="absolute inset-0 w-full h-full object-cover transform scale-[1.01] group-hover:scale-105 transition-transform duration-700 ease-out"
                            >
                              <source src={video.src} type="video/mp4" />
                            </video>
                            <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/10 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>
                          </div>
                        </>
                      );
                      
                      return video.href ? (
                        <a key={idx} href={video.href} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-3 lg:gap-4 group w-full cursor-pointer">
                          {content}
                        </a>
                      ) : (
                        <div key={idx} className="flex flex-col items-center gap-3 lg:gap-4 group w-full">
                          {content}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto relative z-10 py-12 lg:py-20">
          {/* Section Header */}
          <motion.div
            className="text-center mb-12 lg:mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
            variants={fadeInUp}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl text-white mb-4 font-bold">
              Follow Us on{' '}
              <span className="bg-gradient-to-r from-purple-400 via-pink-500 to-cyan-400 bg-clip-text text-transparent">
                Social Media
              </span>
            </h2>
            <div className="h-1 w-24 bg-gradient-to-r from-purple-500 to-cyan-500 mx-auto rounded-full" />
            <p className="mt-4 text-zinc-400 max-w-2xl mx-auto">
              Stay updated with our latest projects, design tips, and behind-the-scenes content.
            </p>

            <div className="flex gap-4 flex-wrap justify-center mt-8">
              {[
                {
                  icon: <Instagram className="w-5 h-5 text-white" />,
                  label: 'Instagram',
                  url: 'https://www.instagram.com/zumify_hawaii/',
                },
                {
                  icon: <Facebook className="w-5 h-5 text-white" />,
                  label: 'Facebook',
                  url: 'https://www.facebook.com/profile.php?id=61585515988041',
                },
                {
                  icon: (
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                  ),
                  label: 'X',
                  url: 'https://x.com/zumify_hawaii',
                },
                {
                  icon: <Linkedin className="w-5 h-5 text-white" />,
                  label: 'LinkedIn',
                  url: 'https://www.linkedin.com/company/zumify/',
                },
                {
                  icon: (
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                    </svg>
                  ),
                  label: 'TikTok',
                  url: 'https://www.tiktok.com/@zumify_hawaii',
                },
                {
                  icon: <Youtube className="w-5 h-5 text-white" />,
                  label: 'YouTube',
                  url: 'https://www.youtube.com/@zumify_hawaii',
                },
              ].map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center hover:bg-purple-700 transition-colors flex-shrink-0"
                  aria-label={social.label}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Reels Section */}
        <section className="px-4 sm:px-6 lg:px-8 pb-12 lg:pb-20">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
              {/* Reel 1 */}
              <a
                href="https://www.instagram.com/p/DTysY_jAVDU/"
                target="_blank"
                rel="noopener noreferrer"
                className="relative block rounded-lg overflow-hidden border-2 border-transparent transition-all duration-300 hover:border-purple-500 hover:shadow-[0_0_20px_rgba(168,85,247,0.5)] hover:scale-105 cursor-pointer group w-full max-w-[300px]"
              >
                <img
                  src="/images/reel-1.webp"
                  alt="How to: Create a high converting site!"
                  className="w-full h-auto block"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/70"></div>
              </a>

              {/* Reel 2 */}
              <a
                href="https://www.instagram.com/p/DUChx9okZrw/"
                target="_blank"
                rel="noopener noreferrer"
                className="relative block rounded-lg overflow-hidden border-2 border-transparent transition-all duration-300 hover:border-purple-500 hover:shadow-[0_0_20px_rgba(168,85,247,0.5)] hover:scale-105 cursor-pointer group w-full max-w-[300px]"
              >
                <img
                  src="/images/reel-2.webp"
                  alt="Designers trying to help developers"
                  className="w-full h-auto block"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/70"></div>
              </a>

              {/* Reel 3 */}
              <a
                href="https://www.instagram.com/p/DTiz1ADgd9W/"
                target="_blank"
                rel="noopener noreferrer"
                className="relative block rounded-lg overflow-hidden border-2 border-transparent transition-all duration-300 hover:border-purple-500 hover:shadow-[0_0_20px_rgba(168,85,247,0.5)] hover:scale-105 cursor-pointer group w-full max-w-[300px]"
              >
                <img
                  src="/images/reel-3.webp"
                  alt="Top 3 Programs to become a high end web developer"
                  className="w-full h-auto block"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/70"></div>
              </a>

              {/* Reel 4 */}
              <a
                href="https://www.instagram.com/p/DUMu7xHkU43/"
                target="_blank"
                rel="noopener noreferrer"
                className="relative block rounded-lg overflow-hidden border-2 border-transparent transition-all duration-300 hover:border-purple-500 hover:shadow-[0_0_20px_rgba(168,85,247,0.5)] hover:scale-105 cursor-pointer group w-full max-w-[300px]"
              >
                <img
                  src="/images/Thumbnail5.webp"
                  alt="Instagram Reel"
                  className="w-full h-auto block"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/70"></div>
              </a>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <motion.section
          className="px-4 sm:px-6 lg:px-8 py-12 lg:py-20"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.5 }}
          variants={fadeInUp}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-purple-500/10 via-pink-500/5 to-cyan-500/10 backdrop-blur-xl border border-white/10 rounded-3xl p-8 lg:p-12 shadow-2xl">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Contact Form for Zumify
            </h2>
            <p className="text-lg sm:text-xl text-purple-100 mb-8">
              We're always open to hearing your ideas! Fill out this Contact Form, and we will
              respond as quickly as possible!
            </p>
            <motion.button
              onClick={() => router.push('/contact')}
              className="px-10 py-5 bg-white text-purple-900 rounded-full font-semibold text-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              Contact Us
            </motion.button>
          </div>
        </motion.section>

        <ResponsiveFooter />

        <style>{`
        @keyframes scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-scroll {
          animation: scroll 40s linear infinite;
        }
      `}</style>
      </div>
    </LazyMotion>
  );
}
